import React, { useState } from 'react';
import { X, BookOpen, Tag, Delete, CheckCircle2 } from 'lucide-react';
import { formatCurrency } from '../../utils/currency';
import { CartItem } from '../../types/book.types';
import './DiscountModal.css';

interface DiscountModalProps {
  item: CartItem;
  onClose: () => void;
  /** Porcentaje 0-100 que pasa a ser el descuento directo de la linea. */
  onConfirm: (discount: number) => void;
}

const DISCOUNT_PRESETS = [0, 5, 10, 15, 20, 25, 30, 50];

/**
 * Descuento directo sobre una linea del ticket: un porcentaje sobre el precio
 * unitario del articulo, con preajustes rapidos y teclado tactil. Es el mismo
 * metodo del TPV de referencia (tpv-albatross-4): el valor se guarda en
 * `item.discount` y de ahi lo leen los totales, el cobro y el ticket.
 */
export function DiscountModal({ item, onClose, onConfirm }: DiscountModalProps) {
  const [val, setVal] = useState<string>(item.discount ? item.discount.toString() : '10');

  const handleKeyPress = (char: string) => {
    if (char === 'clear') {
      setVal('0');
    } else if (char === 'backspace') {
      setVal(prev => (prev.length > 1 ? prev.slice(0, -1) : '0'));
    } else {
      setVal(prev => {
        if (prev === '0') return char;
        const next = prev + char;
        if (parseFloat(next) > 100) return '100';
        return next;
      });
    }
  };

  const handleApply = () => {
    let num = parseFloat(val);
    if (isNaN(num)) num = 0;
    num = Math.max(0, Math.min(100, num));
    onConfirm(num);
  };

  const currentDiscount = parseFloat(val) || 0;
  const discountedPrice = item.price * (1 - currentDiscount / 100);

  return (
    <div className="dm-overlay">
      <div className="dm-container">
        <button onClick={onClose} className="dm-close" aria-label="Cerrar">
          <X style={{ width: '1.5rem', height: '1.5rem' }} />
        </button>

        {/* Izquierda: articulo y efecto del descuento */}
        <div className="dm-left">
          <div>
            <span className="dm-badge">Descuento Directo</span>

            <div className="dm-book">
              <div className="dm-book-cover">
                <BookOpen style={{ width: '2rem', height: '2rem', marginBottom: '0.5rem', opacity: 0.8 }} />
                <span className="dm-book-cover-title">{item.title}</span>
              </div>
              <div className="dm-book-info">
                <h4 className="dm-book-title">{item.title}</h4>
                {item.author && <p className="dm-book-author">{item.author}</p>}
                {item.isbn && <p className="dm-book-isbn">ISBN: {item.isbn}</p>}
              </div>
            </div>

            <div className="dm-prices">
              <div className="dm-prices-row dm-prices-row--border">
                <span className="dm-prices-label">Precio Original</span>
                <span className="dm-prices-original">{formatCurrency(item.price)}</span>
              </div>
              <div className="dm-prices-row">
                <span className="dm-prices-label dm-prices-label--accent">Con Descuento ({currentDiscount}%)</span>
                <span className="dm-prices-final">{formatCurrency(discountedPrice)}</span>
              </div>
              <div className="dm-prices-row dm-prices-row--dashed">
                <span className="dm-prices-label dm-prices-label--dark">Ahorro total</span>
                <span className="dm-prices-saving">-{formatCurrency(item.price - discountedPrice)}</span>
              </div>
            </div>
          </div>

          <p className="dm-note">
            * El descuento directo se aplicará individualmente sobre el precio unitario del artículo en esta venta.
          </p>
        </div>

        {/* Derecha: porcentaje, preajustes y teclado */}
        <div className="dm-right">
          <div>
            <h3 className="dm-heading">
              <Tag style={{ width: '1.25rem', height: '1.25rem', color: '#E07A5F' }} />
              Configurar Porcentaje
            </h3>

            <div className="dm-display">
              <span className="dm-display-label">Porcentaje a Aplicar</span>
              <div className="dm-display-value">
                <input type="text" value={val} readOnly className="dm-display-input" />
                <span className="dm-display-percent">%</span>
              </div>
            </div>

            <div className="dm-section">
              <span className="dm-section-label">Preajustes Rápidos</span>
              <div className="dm-presets">
                {DISCOUNT_PRESETS.map(preset => (
                  <button
                    key={preset}
                    onClick={() => setVal(preset.toString())}
                    className={`dm-preset${parseFloat(val) === preset ? ' dm-preset--active' : ''}`}
                  >
                    {preset === 0 ? 'Sin Dto' : `${preset}%`}
                  </button>
                ))}
              </div>
            </div>

            <div className="dm-section">
              <span className="dm-section-label">Teclado Numérico</span>
              <div className="dm-keypad">
                {['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'].map(digit => (
                  <button key={digit} onClick={() => handleKeyPress(digit)} className="dm-key">
                    {digit}
                  </button>
                ))}
                <button onClick={() => handleKeyPress('backspace')} className="dm-key dm-key--back" aria-label="Borrar un dígito">
                  <Delete style={{ width: '1.5rem', height: '1.5rem' }} />
                </button>
                <button onClick={() => handleKeyPress('clear')} className="dm-key dm-key--clear">
                  Borrar
                </button>
              </div>
            </div>
          </div>

          <button onClick={handleApply} className="dm-confirm">
            <CheckCircle2 style={{ width: '1.25rem', height: '1.25rem', color: '#81B29A' }} />
            Confirmar Descuento
          </button>
        </div>
      </div>
    </div>
  );
}
